import QRCode from "qrcode";
import { SAR_SVG } from "./custodyReceipt";
import { letterheadHTML } from "../components/Letterhead";

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const money = (n) => `${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${SAR_SVG}`;
const STYLE = `@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}
  @page{size:A4;margin:14mm}body{font-family:SaudiRiyal,Tahoma,Arial;color:#0f172a;font-size:13px}h1{font-size:20px;margin:8px 0}
  table{width:100%;border-collapse:collapse;margin-top:8px}th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:center}th{background:#eff6ff;color:#1d4ed8}
  .r{text-align:right}.tot td{font-weight:800}.meta{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}`;

// ZATCA simplified-invoice QR (TLV: seller, VAT no, timestamp, total, VAT).
const zatcaQR = async (seller, vatNo, ts, total, vat) => {
  const enc = new TextEncoder();
  const parts = [seller, vatNo, ts, Number(total).toFixed(2), Number(vat).toFixed(2)].map((v, i) => {
    const b = enc.encode(String(v || ""));
    return [i + 1, b.length, ...b];
  }).flat();
  const b64 = btoa(String.fromCharCode(...parts));
  return QRCode.toDataURL(b64, { margin: 1, width: 150 });
};

const open = (title, body, pre) => {
  const w = pre || window.open("", "_blank");
  if (!w) return false;
  w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>${esc(title)}</title><style>${STYLE}</style></head><body>${body}<script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
  return true;
};

export const printRentalInvoice = async (c, co) => {
  const win = window.open("", "_blank");
  if (!win) return false;
  return open(`فاتورة ${c.number}`, await rentalInvoiceBody(c, co), win);
};

export const rentalInvoiceHTML = async (c, co) =>
  `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>فاتورة ${esc(c.number)}</title><style>${STYLE}</style></head><body>${await rentalInvoiceBody(c, co)}</body></html>`;

const rentalInvoiceBody = async (c, co) => {
  const company = co || {};
  const ch = c.charges || {};
  const lines = [[`إيجار مركبة ${c.vehicle_identifier} (${c.start_date} ${c.start_time || ""} → ${c.end_date} ${c.end_time || ""}) — ${c.units} × ${c.rate}`, c.subtotal + (c.discount || 0), c.discount || 0, c.subtotal, c.vat]];
  const vatOn = c.vat > 0;
  [["late", "غرامة تأخير"], ["extra_km_amount", "كيلومترات زائدة"], ["fuel", "وقود ناقص"], ["damage", "أضرار"]].forEach(([k, l]) => {
    if (ch[k] > 0) lines.push([l, ch[k], 0, ch[k], vatOn ? Math.round(ch[k] * 15) / 100 : 0]);
  });
  if (ch.violations > 0) lines.push(["مخالفات مرورية (غير خاضعة للضريبة)", ch.violations, 0, ch.violations, 0]);
  const sub = lines.reduce((s, l) => s + l[3], 0);
  const vat = lines.reduce((s, l) => s + l[4], 0);
  const total = sub + vat;
  const issued = c.closed_at || c.created_at || new Date().toISOString();
  const qr = await zatcaQR(company.name, company.vat_number, issued, total, vat);
  const rows = lines.map((l, i) => `<tr><td>${i + 1}</td><td class="r">${esc(l[0])}</td><td>${money(l[1])}</td><td>${money(l[2])}</td><td>${money(l[3])}</td><td>${money(l[4])}</td><td>${money(l[3] + l[4])}</td></tr>`).join("");
  return (`${letterheadHTML(company)}
    <div class="meta"><div><h1>${vatOn ? "فاتورة ضريبية مبسطة" : "فاتورة"}</h1>
      <div>رقم الفاتورة: <b>INV-${esc(c.number)}</b></div><div>التاريخ: ${esc(issued.slice(0, 10))}</div>
      <div>الرقم الضريبي للمنشأة: ${esc(company.vat_number || "—")}</div>
      <div style="margin-top:6px">العميل: <b>${esc(c.renter_name)}</b> · ${esc(c.renter_id_number || "")} · ${esc(c.renter_phone || "")}</div></div>
      <img src="${qr}" style="width:120px"/></div>
    <table><thead><tr><th>#</th><th>البيان</th><th>المبلغ</th><th>الخصم</th><th>الخاضع</th><th>الضريبة 15%</th><th>الإجمالي</th></tr></thead><tbody>${rows}</tbody></table>
    <table style="width:50%;margin-right:auto"><tr><td class="r">الإجمالي قبل الضريبة</td><td>${money(sub)}</td></tr><tr><td class="r">ضريبة القيمة المضافة</td><td>${money(vat)}</td></tr>
      <tr class="tot"><td class="r">الإجمالي المستحق</td><td>${money(total)}</td></tr><tr><td class="r">المدفوع</td><td>${money(c.paid)}</td></tr><tr class="tot"><td class="r">المتبقي</td><td>${money(c.balance)}</td></tr></table>`);
};

export const printCustomerStatement = (st, co) => {
  const company = co || {};
  const rows = st.contracts.map((c) => `<tr><td>${esc(c.number)}</td><td>${esc(c.vehicle_identifier)}</td><td>${esc(c.start_date)} → ${esc(c.end_date)}</td>
    <td>${money(c.grand_total)}</td><td>${money(c.paid)}</td><td>${money(c.balance)}</td><td>${money(c.deposit_held)}</td><td>${c.status === "active" ? "نشط" : "منتهي"}</td></tr>`).join("")
    || `<tr><td colspan="8">لا توجد عقود</td></tr>`;
  const pays = st.contracts.flatMap((c) => (c.payments || []).map((p) => ({ ...p, number: c.number }))).sort((a, b) => (a.date > b.date ? 1 : -1));
  const KIND = { payment: "دفعة إيجار", deposit: "استلام تأمين", deposit_refund: "رد تأمين", deposit_apply: "خصم من التأمين" };
  const prow = pays.map((p) => `<tr><td>${esc(p.date)}</td><td>${esc(p.number)}</td><td>${KIND[p.kind] || ""}</td><td>${money(p.amount)}</td><td class="r">${esc(p.note || "")}</td></tr>`).join("")
    || `<tr><td colspan="5">لا توجد حركات</td></tr>`;
  const t = st.totals;
  return open(`كشف حساب ${st.customer.name}`, `${letterheadHTML(company)}<h1>كشف حساب عميل تأجير</h1>
    <div>العميل: <b>${esc(st.customer.name)}</b> · ${esc(st.customer.cr_number || st.customer.id_number || "")} · ${esc(st.customer.phone || "")} · تاريخ الكشف: ${new Date().toISOString().slice(0, 10)}</div>
    <table><thead><tr><th>العقد</th><th>المركبة</th><th>المدة</th><th>الإجمالي</th><th>المدفوع</th><th>المتبقي</th><th>التأمين</th><th>الحالة</th></tr></thead><tbody>${rows}</tbody>
    <tfoot><tr class="tot"><td colspan="3">الإجمالي</td><td>${money(t.grand_total)}</td><td>${money(t.paid)}</td><td>${money(t.balance)}</td><td>${money(t.deposit_held)}</td><td></td></tr></tfoot></table>
    <h3 style="margin-top:16px">الحركات المالية</h3><table><thead><tr><th>التاريخ</th><th>العقد</th><th>النوع</th><th>المبلغ</th><th>ملاحظة</th></tr></thead><tbody>${prow}</tbody></table>`);
};
